import {
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import CloseIcon from '@mui/icons-material/Close';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';

import { streamLive, streamMyLive } from '../api/live';
import type { ReplayTimeline, TraitFrame } from '../replay/buildTimeline';
import { loadReplayTimeline, type ReplayLoad } from '../replay/loadReplayTimeline';
import { TRAIT_KEYS, getRule } from '../replay/traitScoring';

// Lazy so three.js + react-three-fiber (~600 KB) only load when a user
// actually opens a replay with a 3D trajectory — keeps them out of the
// main bundle.
const HandTrajectory3D = lazy(() =>
  import('./HandTrajectory3D').then((m) => ({ default: m.HandTrajectory3D })),
);

/**
 * In-browser session replay. Fetches the session's raw NDJSON via a
 * caller-supplied presign thunk, recomputes the capture-app's live
 * visuals with the ported DSP (see src/replay/*), and plays them back
 * with a transport. Mirrors the recording screen: a rolling 5-band EEG
 * chart, a hand-acceleration trace, and the 6 trait %-bars.
 */

const BAND_WINDOW_S = 30; // rolling history shown on the band chart
const ACCEL_WINDOW_S = 4; // rolling history shown on the hand chart

const BAND_SERIES = [
  { key: 'delta', color: '#5c6bc0' },
  { key: 'theta', color: '#26a69a' },
  { key: 'alpha', color: '#66bb6a' },
  { key: 'beta', color: '#ffa726' },
  { key: 'gamma', color: '#ef5350' },
] as const;

type Stage =
  | 'loading'
  | 'building'
  | 'ready'
  | 'error';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Header line, e.g. "Knot tying · Reto · Jun 19, 10:00". */
  title: string;
  /**
   * VOD mode: presign + fetch a finished session's NDJSON once. Omit
   * when `live` is set.
   */
  fetchUrl?: () => Promise<{ url: string }>;
  /**
   * Live mode: follow the in-progress session's chunks as the recording
   * streams in. Mutually exclusive with `fetchUrl`.
   *
   * Tenant-admin viewer passes `tenantId` (watches any session in the
   * tenant). Participant viewer passes `mine: true` (the server proves
   * they own the session) and no `tenantId`.
   */
  live?:
    | { tenantId: string; sessionId: string }
    | { mine: true; sessionId: string };
}

// While following live, keep the playhead within this many seconds of the
// leading edge — if it falls further behind (a catch-up batch arrived),
// jump it near the edge instead of crawling there at 1×.
const LIVE_MAX_LAG_S = 3;

function fmt(t: number): string {
  if (!Number.isFinite(t) || t < 0) t = 0;
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Last frame with `.t <= t` (binary search over a time-sorted array). */
function frameAt<T extends { t: number }>(arr: T[], t: number): T | null {
  let lo = 0;
  let hi = arr.length - 1;
  let res: T | null = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid].t <= t) {
      res = arr[mid];
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return res;
}

/** Slice the trailing window (fromT, toT] from a time-sorted array. */
function windowSlice<T extends { t: number }>(
  arr: T[],
  fromT: number,
  toT: number,
): T[] {
  const out: T[] = [];
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i].t > toT) continue;
    if (arr[i].t < fromT) break;
    out.push(arr[i]);
  }
  out.reverse();
  return out;
}

export function SessionReplayDialog({
  open,
  onClose,
  title,
  fetchUrl,
  live,
}: Props) {
  const [stage, setStage] = useState<Stage>('loading');
  const [error, setError] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<ReplayTimeline | null>(null);
  // VOD: bytes of the recording read so far (it is read as a stream).
  const [loadedBytes, setLoadedBytes] = useState(0);

  // Playback
  const [playing, setPlaying] = useState(false);
  const [tSec, setTSec] = useState(0);
  const [speed, setSpeed] = useState(1);
  const rafRef = useRef<number | undefined>(undefined);
  const lastTsRef = useRef<number | undefined>(undefined);

  // Live mode: follow the leading edge until the user scrubs back.
  const [follow, setFollow] = useState(true);
  const [liveEnded, setLiveEnded] = useState(false);
  const accumRef = useRef('');
  const cursorRef = useRef(0);

  // Keep the latest fetchUrl in a ref so the VOD load effect can call it
  // WITHOUT taking it as a dependency. Callers pass an inline arrow that
  // gets a new identity on every parent re-render — and the Activity page
  // (5 s live-sessions poll) + Dashboard (insights poll) now re-render on
  // a timer, which otherwise re-fires the load effect mid-playback and
  // makes the replay reload/restart every few seconds.
  const fetchUrlRef = useRef(fetchUrl);
  useEffect(() => {
    fetchUrlRef.current = fetchUrl;
  });

  // Load: presign → stream the recording into the timeline, off the main
  // thread (replay/loadReplayTimeline.ts). VOD mode only. A long recording
  // is more NDJSON than the browser can hold as one string, so it is never
  // read whole (SCALE-PORTAL-REPLAY-STREAMING).
  useEffect(() => {
    const getUrl = fetchUrlRef.current;
    if (!open || live || !getUrl) return;
    let cancelled = false;
    let load: ReplayLoad | null = null;
    setStage('loading');
    setError(null);
    setTimeline(null);
    setLoadedBytes(0);
    setPlaying(false);
    setTSec(0);
    (async () => {
      try {
        const { url } = await getUrl();
        if (cancelled) return;
        load = loadReplayTimeline(url, {
          onProgress: (bytes) => !cancelled && setLoadedBytes(bytes),
          onBuilding: () => !cancelled && setStage('building'),
        });
        const tl = await load.promise;
        if (cancelled) return;
        setTimeline(tl);
        setTSec(0);
        setStage('ready');
        setPlaying(true);
      } catch (e) {
        if (!cancelled) {
          setError(
            (e as Error).message ||
              'Could not load this recording for replay.',
          );
          setStage('error');
        }
      }
    })();
    return () => {
      cancelled = true;
      load?.cancel();
    };
    // fetchUrl is intentionally read via fetchUrlRef (see above) and NOT a
    // dependency — an unstable inline prop must not retrigger the load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, live]);

  // Live mode: poll chunks since the cursor, accumulate, and rebuild the
  // timeline in a Web Worker (off the UI thread). Follow the leading
  // edge unless the user has scrubbed back.
  useEffect(() => {
    if (!open || !live) return;
    let cancelled = false;
    accumRef.current = '';
    cursorRef.current = 0;
    setStage('loading');
    setError(null);
    setTimeline(null);
    setLiveEnded(false);
    setFollow(true);
    setTSec(0);
    setPlaying(false);

    // Build the timeline off-thread so a growing session never freezes
    // the page. Coalesce: while one build runs, stash the latest text
    // and rebuild once it returns — builds never pile up.
    const worker = new Worker(
      new URL('../replay/replay.worker.ts', import.meta.url),
      { type: 'module' },
    );
    let busy = false;
    let pendingText: string | null = null;
    const requestBuild = (text: string) => {
      if (busy) {
        pendingText = text;
        return;
      }
      busy = true;
      worker.postMessage(text);
    };
    worker.onmessage = (
      e: MessageEvent<{ ok: boolean; timeline?: ReplayTimeline }>,
    ) => {
      if (cancelled) return;
      if (e.data.ok && e.data.timeline) {
        setTimeline(e.data.timeline);
        setStage('ready');
      }
      busy = false;
      if (pendingText !== null) {
        const next = pendingText;
        pendingText = null;
        busy = true;
        worker.postMessage(next);
      }
    };

    // Server pushes each chunk over SSE the instant the device uploads
    // it — no polling. Reconnection + token refresh are handled inside
    // streamLive; we just accumulate and (coalesced) rebuild.
    const handlers = {
      onChunk: (ndjson: string, cursor: number) => {
        if (cancelled) return;
        accumRef.current += ndjson;
        cursorRef.current = cursor;
        requestBuild(accumRef.current);
      },
      onEnded: () => {
        if (cancelled) return;
        setLiveEnded(true);
        setFollow(false);
      },
    };
    // Participant scope ('mine') streams their own session; tenant-admin
    // scope streams by tenant. Same mechanics either way.
    const stop =
      'mine' in live
        ? streamMyLive(live.sessionId, 0, handlers)
        : streamLive(live.tenantId, live.sessionId, 0, handlers);
    return () => {
      cancelled = true;
      stop();
      worker.terminate();
    };
    // Primitive deps so a fresh `live` object reference from the parent
    // (it re-renders on its own poll) doesn't restart the stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, 'mine' in (live ?? {}), (live as { tenantId?: string })?.tenantId, live?.sessionId]);

  // While following live, EASE the playhead toward the leading edge with
  // a rAF loop instead of snapping to it each poll. The live edge is
  // read from a ref (updated whenever the timeline rebuilds) so this
  // loop runs CONTINUOUSLY — depending on `timeline` here would restart
  // the loop on every poll and cause a stutter once a second.
  const durationRef = useRef(0);
  useEffect(() => {
    if (timeline) durationRef.current = timeline.durationS;
  }, [timeline]);
  const liveRafRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!live || !follow) return;
    let mounted = true;
    let last: number | undefined;
    const step = (now: number) => {
      if (!mounted) return;
      const dt = ((now - (last ?? now)) / 1000) * speed;
      last = now;
      const target = durationRef.current;
      if (target > 0) {
        setTSec((prev) => {
          let next = prev + dt;
          if (target - next > LIVE_MAX_LAG_S) next = target - 1;
          return Math.min(next, target);
        });
      }
      liveRafRef.current = requestAnimationFrame(step);
    };
    liveRafRef.current = requestAnimationFrame(step);
    return () => {
      mounted = false;
      if (liveRafRef.current) cancelAnimationFrame(liveRafRef.current);
    };
  }, [live, follow, speed]);


  // rAF playback loop. Disabled while live-following (the playhead is
  // pinned to the leading edge by the poll effect instead).
  useEffect(() => {
    if (!playing || !timeline) return;
    if (live && follow) return;
    let mounted = true;
    lastTsRef.current = undefined;
    const step = (now: number) => {
      if (!mounted) return;
      const last = lastTsRef.current ?? now;
      const dt = ((now - last) / 1000) * speed;
      lastTsRef.current = now;
      setTSec((prev) => {
        const next = prev + dt;
        if (next >= timeline.durationS) {
          setPlaying(false);
          return timeline.durationS;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      mounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, speed, timeline, live, follow]);

  const atEnd = timeline !== null && tSec >= timeline.durationS;

  const traitFrame: TraitFrame | null = useMemo(
    () => (timeline ? frameAt(timeline.traits, tSec) : null),
    [timeline, tSec],
  );
  const bandWindow = useMemo(
    () =>
      timeline
        ? windowSlice(timeline.bands, tSec - BAND_WINDOW_S, tSec)
        : [],
    [timeline, tSec],
  );
  const accelWindow = useMemo(
    () =>
      timeline
        ? windowSlice(timeline.accel, tSec - ACCEL_WINDOW_S, tSec)
        : [],
    [timeline, tSec],
  );
  const currentPhase = useMemo(
    () => timeline?.phases.find((p) => tSec >= p.startS && tSec <= p.endS),
    [timeline, tSec],
  );

  const togglePlay = () => {
    if (atEnd) {
      setTSec(0);
      setPlaying(true);
    } else {
      setPlaying((p) => !p);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{live ? 'Live session' : 'Session replay'}</span>
          {live && !liveEnded && (
            <Chip label="● LIVE" size="small" color="error" />
          )}
          {live && liveEnded && (
            <Chip label="Recording ended" size="small" variant="outlined" />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {stage === 'loading' && (
          <Stack alignItems="center" sx={{ py: 6 }} spacing={2}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {live
                ? 'Connecting to the live stream…'
                : loadedBytes > 0
                  ? `Reading the recording… ${Math.round(loadedBytes / 1e6)} MB`
                  : 'Loading recording…'}
            </Typography>
          </Stack>
        )}

        {stage === 'building' && (
          <Stack alignItems="center" sx={{ py: 6 }} spacing={2}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Recomputing trace…
            </Typography>
          </Stack>
        )}

        {stage === 'error' && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error ?? 'Replay failed.'}
          </Alert>
        )}

        {stage === 'ready' && timeline && (
          <Stack spacing={2}>
            {timeline.durationS === 0 ? (
              <Stack alignItems="center" sx={{ py: 4 }} spacing={2}>
                {live && !liveEnded && <CircularProgress />}
                <Alert severity="info">
                  {live && !liveEnded
                    ? 'Waiting for the first samples from the device…'
                    : 'This recording has no replayable sensor samples.'}
                </Alert>
              </Stack>
            ) : (
              <>
                {/* Transport */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ flexWrap: 'wrap' }}
                >
                  {!(live && follow) && (
                    <IconButton color="primary" onClick={togglePlay}>
                      {atEnd ? (
                        <ReplayIcon />
                      ) : playing ? (
                        <PauseIcon />
                      ) : (
                        <PlayArrowIcon />
                      )}
                    </IconButton>
                  )}
                  <Typography
                    variant="body2"
                    sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 92 }}
                  >
                    {fmt(tSec)} / {fmt(timeline.durationS)}
                  </Typography>
                  <Slider
                    size="small"
                    min={0}
                    max={timeline.durationS}
                    step={0.1}
                    value={Math.min(tSec, timeline.durationS)}
                    onChange={(_, v) => {
                      setTSec(v as number);
                      // Scrubbing in live mode drops out of follow.
                      if (live) setFollow(false);
                    }}
                    sx={{
                      flex: 1,
                      minWidth: 160,
                      // The playhead updates ~60×/s during playback. MUI's
                      // default `left` transition on the thumb/track keeps
                      // getting re-targeted before it can paint, so the
                      // thumb looks pinned at 0 and only snaps to the end
                      // when playback stops. Disable it so the bar tracks
                      // the playhead live (seeking is still fine, just
                      // instant instead of eased).
                      '& .MuiSlider-thumb, & .MuiSlider-track': {
                        transition: 'none',
                      },
                    }}
                    aria-label="Seek"
                  />
                  {live && !liveEnded && (
                    <Button
                      size="small"
                      variant={follow ? 'contained' : 'outlined'}
                      color={follow ? 'error' : 'primary'}
                      onClick={() => setFollow(true)}
                      sx={{ minWidth: 88 }}
                    >
                      {follow ? '● LIVE' : 'Go live'}
                    </Button>
                  )}
                  {!(live && follow) && (
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={speed}
                      onChange={(_, v) => v && setSpeed(v)}
                    >
                      <ToggleButton value={1}>1×</ToggleButton>
                      <ToggleButton value={2}>2×</ToggleButton>
                      <ToggleButton value={4}>4×</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                  {currentPhase && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: 'action.selected',
                        textTransform: 'capitalize',
                      }}
                    >
                      {currentPhase.phase}
                    </Typography>
                  )}
                </Stack>

                {/* Trait bars */}
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Traits
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(3, 1fr)',
                      },
                      gap: 1.5,
                      mt: 0.5,
                    }}
                  >
                    {TRAIT_KEYS.map((key) => {
                      const rule = getRule(key);
                      const pct = traitFrame ? traitFrame[key] : null;
                      return (
                        <TraitBar
                          key={key}
                          label={rule.label}
                          color={rule.color}
                          pct={pct}
                        />
                      );
                    })}
                  </Box>
                </Box>

                {/* EEG bands */}
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    EEG bands (relative power)
                  </Typography>
                  {timeline.hasEeg ? (
                    <Box sx={{ height: 150 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={bandWindow}
                          margin={{ top: 6, right: 12, bottom: 0, left: -16 }}
                        >
                          <CartesianGrid
                            stroke="#e5e7eb"
                            strokeDasharray="3 3"
                          />
                          <XAxis
                            type="number"
                            dataKey="t"
                            domain={[
                              Math.max(0, tSec - BAND_WINDOW_S),
                              Math.max(BAND_WINDOW_S, tSec),
                            ]}
                            tick={{ fontSize: 10 }}
                            tickFormatter={(v: number) => `${v.toFixed(0)}s`}
                          />
                          <YAxis
                            domain={[0, 1]}
                            tick={{ fontSize: 10 }}
                            width={32}
                          />
                          {BAND_SERIES.map((b) => (
                            <Line
                              key={b.key}
                              type="monotone"
                              dataKey={b.key}
                              name={b.key}
                              stroke={b.color}
                              strokeWidth={1.75}
                              dot={false}
                              isAnimationActive={false}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                      <BandLegend />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No EEG in this recording.
                    </Typography>
                  )}
                </Box>

                {/* Hand motion */}
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Hand motion (acceleration, g)
                  </Typography>
                  {timeline.hasImu ? (
                    <Box sx={{ height: 130 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={accelWindow}
                          margin={{ top: 6, right: 12, bottom: 0, left: -16 }}
                        >
                          <CartesianGrid
                            stroke="#e5e7eb"
                            strokeDasharray="3 3"
                          />
                          <XAxis
                            type="number"
                            dataKey="t"
                            domain={[
                              Math.max(0, tSec - ACCEL_WINDOW_S),
                              Math.max(ACCEL_WINDOW_S, tSec),
                            ]}
                            tick={{ fontSize: 10 }}
                            tickFormatter={(v: number) => `${v.toFixed(0)}s`}
                          />
                          <YAxis tick={{ fontSize: 10 }} width={32} />
                          <Line
                            type="monotone"
                            dataKey="left"
                            name="left"
                            stroke="#4c8dff"
                            strokeWidth={1.5}
                            dot={false}
                            connectNulls
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="right"
                            name="right"
                            stroke="#ef5350"
                            strokeWidth={1.5}
                            dot={false}
                            connectNulls
                            isAnimationActive={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                      <Stack direction="row" spacing={2} sx={{ pl: 2 }}>
                        <LegendDot color="#4c8dff" label="left hand" />
                        <LegendDot color="#ef5350" label="right hand" />
                      </Stack>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No hand-motion sensors in this recording.
                    </Typography>
                  )}
                </Box>

                {/* Hand trajectory — interactive 3D, revealed along the
                    clock. Only when host-fusion streams were recorded.
                    Omitted during a LIVE session: the trajectory is a
                    cumulative double-integration with per-phase bias
                    correction, so it can't be appended incrementally —
                    it would rebuild + re-centre + re-frame on every poll
                    (off-scale, nothing visible). It renders correctly in
                    the post-session replay once the full recording is in. */}
                {timeline.hasTrajectory && live && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Hand trajectory (3D)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      The 3D hand trajectory is available in the
                      post-session replay (open this session from the
                      Activity timeline once recording ends) — it needs the
                      complete recording to reconstruct correctly.
                    </Typography>
                  </Box>
                )}
                {timeline.trajectorySkipped && !live && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Hand trajectory (3D)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Not shown for a recording this long: the 3D hand path
                      is rebuilt for up to about half an hour of hand
                      motion. The hand-motion trace above covers the whole
                      recording.
                    </Typography>
                  </Box>
                )}
                {timeline.hasTrajectory && !live && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Hand trajectory (3D)
                    </Typography>
                    <Suspense
                      fallback={
                        <Stack alignItems="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} />
                        </Stack>
                      }
                    >
                      <HandTrajectory3D
                        trajectories={timeline.trajectories}
                        tSec={tSec}
                      />
                    </Suspense>
                  </Box>
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TraitBar({
  label,
  color,
  pct,
}: {
  label: string;
  color: string;
  pct: number | null;
}) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="caption" sx={{ color, fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {pct === null ? '—' : `${pct}%`}
        </Typography>
      </Stack>
      <Box
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: 'action.hover',
          overflow: 'hidden',
          mt: 0.25,
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${pct ?? 0}%`,
            bgcolor: color,
            transition: 'width 0.2s linear',
          }}
        />
      </Box>
    </Box>
  );
}

function BandLegend() {
  return (
    <Stack direction="row" spacing={1.5} sx={{ pl: 2, flexWrap: 'wrap' }}>
      {BAND_SERIES.map((b) => (
        <LegendDot key={b.key} color={b.color} label={b.key} />
      ))}
    </Stack>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box
        sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'capitalize' }}
      >
        {label}
      </Typography>
    </Stack>
  );
}
